import { Injectable } from '@angular/core';

interface MemoryEntry<T> {
  readonly expiresAt: number;
  readonly value?: T;
  readonly promise?: Promise<T>;
}

interface StoredEntry<T> {
  readonly expiresAt: number;
  readonly value: T;
}

type StorageResult<T> =
  | { readonly hit: true; readonly expiresAt: number; readonly value: T }
  | { readonly hit: false };

const STORAGE_PREFIX = 'luxury-public-cache:';

/**
 * Cache chica para datos públicos/read-only del storefront.
 *
 * Intencionalmente NO se usa para sesión, carrito, checkout ni endpoints `/me/*`:
 * esos dependen del usuario y deben seguir frescos.
 */
@Injectable({ providedIn: 'root' })
export class PublicDataCacheService {
  private readonly memory = new Map<string, MemoryEntry<unknown>>();

  /** Cachea en memoria y en `sessionStorage`: sobrevive a recargar la pestaña. */
  get<T>(key: string, ttlMs: number, loader: () => Promise<T>): Promise<T> {
    return this.resolve(key, ttlMs, loader, true);
  }

  /**
   * Cachea SOLO en memoria, para respuestas que dependen de quién pregunta
   * (precios por segmento del socio).
   *
   * Alcanza para que volver a una pantalla ya visitada no vuelva a pedirlas,
   * que es lo que se siente lento, sin dejar datos del socio escritos en el
   * storage de la pestaña. La clave debe incluir la identidad de la sesión.
   */
  getInMemory<T>(key: string, ttlMs: number, loader: () => Promise<T>): Promise<T> {
    return this.resolve(key, ttlMs, loader, false);
  }

  deletePrefix(prefix: string): void {
    for (const key of [...this.memory.keys()]) {
      if (key.startsWith(prefix)) this.memory.delete(key);
    }

    const storage = this.storage();
    if (!storage) return;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const storageKey = storage.key(index);
      if (storageKey?.startsWith(`${STORAGE_PREFIX}${prefix}`)) {
        storage.removeItem(storageKey);
      }
    }
  }

  private async resolve<T>(
    key: string,
    ttlMs: number,
    loader: () => Promise<T>,
    persist: boolean,
  ): Promise<T> {
    const now = Date.now();
    const memoryEntry = this.memory.get(key) as MemoryEntry<T> | undefined;

    if (memoryEntry && memoryEntry.expiresAt > now) {
      if (memoryEntry.value !== undefined) return memoryEntry.value;
      if (memoryEntry.promise) return memoryEntry.promise;
    }

    if (persist) {
      const stored = this.read<T>(key, now);
      if (stored.hit) {
        this.memory.set(key, { expiresAt: stored.expiresAt, value: stored.value });
        return stored.value;
      }
    }

    const pending = loader().then(
      (value) => {
        this.remember(key, ttlMs, value, persist);
        return value;
      },
      (error: unknown) => {
        const current = this.memory.get(key) as MemoryEntry<T> | undefined;
        if (current?.promise === pending) {
          this.memory.delete(key);
        }
        throw error;
      },
    );
    this.memory.set(key, { expiresAt: now + ttlMs, promise: pending });
    return pending;
  }

  private remember(key: string, ttlMs: number, value: unknown, persist: boolean): void {
    const expiresAt = Date.now() + ttlMs;
    this.memory.set(key, { expiresAt, value });
    if (persist) this.write(key, { expiresAt, value });
  }

  private read<T>(key: string, now: number): StorageResult<T> {
    const storage = this.storage();
    if (!storage) return { hit: false };

    try {
      const raw = storage.getItem(this.storageKey(key));
      if (!raw) return { hit: false };

      const parsed = JSON.parse(raw) as Partial<StoredEntry<T>> | null;
      if (
        !parsed ||
        typeof parsed.expiresAt !== 'number' ||
        parsed.expiresAt <= now ||
        !('value' in parsed)
      ) {
        storage.removeItem(this.storageKey(key));
        return { hit: false };
      }
      const value = (parsed as StoredEntry<T>).value;
      return { hit: true, expiresAt: parsed.expiresAt, value };
    } catch {
      storage.removeItem(this.storageKey(key));
      return { hit: false };
    }
  }

  private write(key: string, entry: StoredEntry<unknown>): void {
    const storage = this.storage();
    if (!storage) return;

    try {
      storage.setItem(this.storageKey(key), JSON.stringify(entry));
    } catch {
      // La cache es oportunista: si el navegador bloquea o llena storage, seguimos sin romper la UI.
    }
  }

  private storageKey(key: string): string {
    return `${STORAGE_PREFIX}${key}`;
  }

  private storage(): Storage | null {
    try {
      return typeof window === 'undefined' ? null : window.sessionStorage;
    } catch {
      return null;
    }
  }
}
